import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, api, keys, type Uuid } from "@shared/api";
import { Dialog, Field } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";

/** Ник длиннее не примет сервер — как в Discord. */
const MAX = 32;

/**
 * Ник на сервере — как в Discord: имя человека только здесь, поверх общего.
 * Пусто — снова общее имя.
 */
export function NicknameDialog({
  serverId,
  user,
  onClose,
}: {
  serverId: Uuid;
  user: { id: Uuid; name: string; nickname?: string; self?: boolean };
  onClose: () => void;
}) {
  const cache = useQueryClient();
  const [value, setValue] = useState(user.nickname ?? "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function save(next: string | null) {
    setBusy(true);
    setProblem(null);
    try {
      await api.servers.nickname(serverId, user.id, next?.trim() || null);
      await cache.invalidateQueries({ queryKey: keys.members(serverId) });
      onClose();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Не вышло сменить ник");
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={user.self ? "Ник на сервере" : `Ник ${user.name}`}
      onClose={onClose}
      actions={
        <>
          {user.nickname && (
            <Button variant="danger" type="button" disabled={busy} onClick={() => void save(null)}>
              Сбросить
            </Button>
          )}
          <Button variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={busy} onClick={() => void save(value)}>
            Сохранить
          </Button>
        </>
      }
    >
      <Field label="Ник" hint="Виден всем на этом сервере. Пусто — общее имя.">
        <Input
          autoFocus
          value={value}
          maxLength={MAX}
          placeholder={user.name}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void save(value);
          }}
        />
      </Field>
      {problem && <p className="small danger-text" style={{ margin: 0 }}>{problem}</p>}
    </Dialog>
  );
}
